/**
 * Asset URL Configuration
 *
 * This file centralizes all asset URL configuration for PageMind.
 * It handles CDN URLs, fallbacks, and environment-specific settings.
 */

// Environment configuration
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Asset URL configuration
const ASSETS_CONFIG = {
  // CDN URL (when Cloudflare is properly configured)
  cdn: {
    base: 'https://assets.pagemind.ai',
    videos: 'https://assets.pagemind.ai/hero-videos',
    posters: 'https://assets.pagemind.ai/hero-posters',
    crew: 'https://assets.pagemind.ai/crew-agents'
  },

  // Direct S3 URLs (current working solution)
  s3: {
    base: 'https://fsn1.your-objectstorage.com/pagemindimages',
    videos: 'https://fsn1.your-objectstorage.com/pagemindimages/hero-videos',
    posters: 'https://fsn1.your-objectstorage.com/pagemindimages/hero-posters',
    crew: 'https://fsn1.your-objectstorage.com/pagemindimages/crew-agents'
  },

  // Local URLs (development fallback)
  local: {
    base: '',
    videos: '/hero-videos',
    posters: '/hero-posters',
    crew: '/crew-agents'
  }
};

/**
 * Get the appropriate asset base URLs based on environment
 * Always use CDN now that Cloudflare Worker is deployed
 */
function getAssetUrls() {
  // Always use CDN - it's now properly configured and working
  // The Cloudflare Worker at assets.pagemind.ai proxies to Hetzner S3
  return {
    videos: ASSETS_CONFIG.cdn.videos,
    posters: ASSETS_CONFIG.cdn.posters,
    crew: ASSETS_CONFIG.cdn.crew,
    fallback: ASSETS_CONFIG.s3 // Direct S3 as fallback if CDN fails
  };
}

/**
 * Generate asset URL with fallback support
 */
export function getAssetUrl(type, filename) {
  const urls = getAssetUrls();
  let baseUrl;

  switch(type) {
    case 'video':
      baseUrl = urls.videos;
      break;
    case 'poster':
      baseUrl = urls.posters;
      break;
    case 'crew':
      baseUrl = urls.crew;
      break;
    default:
      baseUrl = urls.videos;
  }

  return `${baseUrl}/${filename}`;
}

/**
 * Generate video URL
 */
export function getVideoUrl(filename) {
  return getAssetUrl('video', filename);
}

/**
 * Generate poster URL
 */
export function getPosterUrl(filename) {
  return getAssetUrl('poster', filename);
}

/**
 * Generate crew asset URL
 */
export function getCrewAssetUrl(filename) {
  return getAssetUrl('crew', filename);
}

/**
 * Get all asset URLs (for debugging)
 */
export function getAssetConfig() {
  return {
    environment: process.env.NODE_ENV,
    isDevelopment,
    isProduction,
    urls: getAssetUrls(),
    config: ASSETS_CONFIG
  };
}

/**
 * Test asset availability
 */
export async function testAssetAvailability(url) {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

export default {
  getAssetUrl,
  getVideoUrl,
  getPosterUrl,
  getCrewAssetUrl,
  getAssetConfig,
  testAssetAvailability
};